/**
 * App Updates Configuration
 *
 * This system manages product updates shown to users in two ways:
 * 1. In-app notification (bottom-left popup) - Only shows updates from the last 30 days
 * 2. What's New modal (in help menu) - Shows all enabled updates as a historical record
 *
 * Users only need to dismiss one notification at a time. Once dismissed, the next
 * recent update (if any) will appear on their next visit.
 *
 * The 'enabled' flag controls whether an update appears anywhere in the system.
 * Set to false to hide outdated or problematic updates without deleting them.
 *
 * IMAGES:
 * 1. Add your image to: packages/client/public/update-images/
 * 2. Reference it as: '/update-images/your-image.png'
 *
 * Example: image: '/update-images/saga-1-0.png'
 */

export interface AppUpdate {
    key: string; // Unique key for localStorage tracking
    title: string; // Title shown in notification
    description: string; // Description text
    image?: string; // Optional image path (e.g., '/updates/feature.png')
    actionUrl?: string; // Optional URL for "Learn More" button
    releaseDate: string; // ISO date string for sorting and notification window
    enabled: boolean; // Whether to include this update in the system
}

// Updates are ordered from newest to oldest
// Add new updates at the beginning of the array
export const APP_UPDATES: AppUpdate[] = [
    {
        key: 'saga-release',
        title: 'Saga 1.0',
        description:
            'We are officially out of beta! Read the release for more information on what we are building next.',
        image: '/update-images/saga-1-0.png',
        actionUrl: 'https://saga.so/blog/introducing-saga',
        releaseDate: '2024-11-14',
        enabled: true,
    },
    {
        key: 'ai-beta',
        title: 'Saga AI (Beta)',
        description:
            'Get creative, write faster, and do better work directly in Saga with the help of a digital AI assistant.',
        image: '/update-images/ai-beta.png',
        actionUrl: 'https://saga.so/updates/saga-ai-beta',
        releaseDate: '2023-04-12',
        enabled: false,
    },
];

// Get all enabled updates that haven't been seen AND are within the notification window
// Constants for better performance
const NOTIFICATION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

export function getUnseenUpdates(seenUpdates: Set<string>): AppUpdate[] {
    const cutoffTime = Date.now() - NOTIFICATION_WINDOW_MS;

    return APP_UPDATES.filter((update) => {
        if (!update.enabled || seenUpdates.has(update.key)) {
            return false;
        }

        // Validate date format and check if within notification window
        try {
            const updateTime = new Date(update.releaseDate).getTime();
            return !isNaN(updateTime) && updateTime >= cutoffTime;
        } catch {
            console.error(`Invalid date format for update ${update.key}: ${update.releaseDate}`);
            return false;
        }
    });
}

// Get the last N enabled updates for the help menu
export function getRecentUpdates(limit: number = 10): AppUpdate[] {
    return APP_UPDATES.filter((update) => update.enabled).slice(0, limit);
}

// Storage key for all update tracking
const UPDATES_STORAGE_KEY = 'saga-updates-tracking';

interface UpdatesStorage {
    version: number;
    seenUpdates: string[];
    lastUpdated: string;
}

// Get the updates storage object
function getUpdatesStorage(): UpdatesStorage {
    try {
        const stored = localStorage.getItem(UPDATES_STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (error) {
        console.error('Error reading updates storage:', error);
    }

    // Return default if nothing stored or error
    return {
        version: 1,
        seenUpdates: [],
        lastUpdated: new Date().toISOString(),
    };
}

// Save the updates storage object
function saveUpdatesStorage(storage: UpdatesStorage): void {
    try {
        localStorage.setItem(UPDATES_STORAGE_KEY, JSON.stringify(storage));
    } catch (error) {
        console.error('Error saving updates storage:', error);
    }
}

// Check if user has seen a specific update
export function hasSeenUpdate(updateKey: string): boolean {
    const storage = getUpdatesStorage();
    return storage.seenUpdates.includes(updateKey);
}

// Mark an update as seen
export function markUpdateAsSeen(updateKey: string): void {
    const storage = getUpdatesStorage();
    if (!storage.seenUpdates.includes(updateKey)) {
        storage.seenUpdates.push(updateKey);
        storage.lastUpdated = new Date().toISOString();
        saveUpdatesStorage(storage);
    }
}

// Get all seen updates (for components that need the full set)
export function getAllSeenUpdates(): Set<string> {
    const storage = getUpdatesStorage();
    return new Set(storage.seenUpdates);
}
